import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS } from '@/nova/theme/tokens';

/* The collapsible, read-only "View final prompt" panel under the single-clip shot list: a quiet text toggle,
   then the compiled prompt, the negative prompt (when the model has one) and the request summary. */

export const Root = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${COLORS.hairline};
`;

export const Toggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${COLORS.textMuted};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.text};
  }
  /* The chevron points down when closed and up when open. */
  svg {
    transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
    transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  }
`;

export const Body = styled.div<{ $pending: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  /* A result being recompiled stays readable, just softened, until the fresh one lands. */
  opacity: ${(p) => (p.$pending ? 0.6 : 1)};
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const BlockLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
`;

/** The compiled prompt text, verbatim (line breaks kept), scrolling past a cap so the pane stays compact. */
export const PromptText = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  max-height: 260px;
  overflow: auto;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${COLORS.text};
`;

export const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 12px;
  margin: 0;
  font-size: 12px;
`;

export const FactTerm = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.textMuted};
`;

export const FactValue = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.text};
`;

export const Note = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textMuted};
`;
